import React, { useContext } from 'react';
import { Box, H3, Copy, Button, Fade, Modal, useMediaQueries } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import themeStyles from './NotificationPopup.style';
import { PopupContext } from '../Popup/PopupContext';
import { ModelExplorationContext } from '../ModelExplorationContext';
import { mapJssFieldsToCTAs } from '../../utils/sitecoreFields';
import CTA from '../CTA';
import { stripMarkdownHeading, styledCompiler } from '../../utils/markdown';

const Container = themeStyles.apply(Box, 'Container');
const Content = themeStyles.apply(Box, 'Content');
const Title = themeStyles.apply(H3, 'Title');
const Description = themeStyles.apply(Copy, 'Description');
const CTASection = themeStyles.apply(Copy, 'CTASection');

const CustomCTA = ({ cta, onClose }) => {
  const { linkField, typeField, iconField, gtmTags } = cta || {};
  return linkField?.value?.href ? (
    <CTA {...cta} />
  ) : (
    <Button
      styling={typeField?.value?.toLowerCase()}
      linkField={linkField?.value}
      typeField={typeField}
      iconField={iconField}
      onClick={onClose}
      {...gtmTags}
    >
      {cta?.linkField?.value?.text}
    </Button>
  );
};

const NotificationPopup = ({ fields, rendering }) => {
  const currentComponentId = rendering?.dataSource?.replace('{', '')?.replace('}', '')?.toLowerCase();
  const { sitecoreContext } = useSitecoreContext() || {};
  const { componentName } = rendering || {};
  const { popupId, setPopupId } = useContext(PopupContext) || {};
  const { bodyText, title, gtmTitle } = fields || {};
  const validCTAs = cta => !!(cta?.linkField?.value?.text && cta?.typeField?.value);
  const ctas = mapJssFieldsToCTAs(fields, gtmTitle?.value).filter(validCTAs);

  const { t } = useTranslation();
  const { isMobile } = useMediaQueries();
  const modelExplorationContext = useContext(ModelExplorationContext) || {};
  const { isDark } = modelExplorationContext;
  const onClose = () => setPopupId(null);
  const isOpen = currentComponentId === popupId;

  if (sitecoreContext.pageEditing)
    return (
      // eslint-disable-next-line i18next/no-literal-string
      <Box>
        Experience Editor: {componentName} {title?.value} {currentComponentId}
      </Box>
    );

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      hasNoPaddingX={isMobile}
      allowClose
      closeAriaLabel={t('Shared.Common.pressToClosePopupAria')}
      closeBtnStyling={isMobile && !isDark ? 'default' : 'circularWhite'}
      iconSize="default"
      backgroundColor={isDark ? 'black' : 'white'}
      data-testid="notification-popup"
    >
      {() => (
        <Fade>
          <Container>
            <Content>
              <Title>{styledCompiler(stripMarkdownHeading(title?.value))}</Title>
              <Description>{styledCompiler(bodyText?.value)}</Description>
            </Content>
            <CTASection>
              {ctas?.map(cta => (
                <CustomCTA key={cta?.linkField?.value?.text} cta={cta} onClose={onClose} />
              ))}
            </CTASection>
          </Container>
        </Fade>
      )}
    </Modal>
  );
};

export default NotificationPopup;
